import { Box, Button } from "@mui/material";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import EmptySearchAndBuy from "./EmptySearchAndBuy";
import { useSearchParams, useNavigate } from "react-router-dom";
import Loader from "../../../shared/components/Loader";
import ActionToast from "../../../shared/components/ActionToast";
import { CustomerBuyerInventoryState, GroupedStockItem } from "../../shared/models/customerBuyerStockItemsModels";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import GroupedStockItemCard from "./GroupedStockItemCard";
import {
  getGroupedStockItems,
  setGroupedStockItems,
  setError,
  setSuccess,
  getFilters,
  changeProductFiltersQuery
} from "../customerBuyerInventorySlice";
import { NotificationMessage } from "../../../shared/models/models";
import Filters from "./Filters";
import useTranslation from "../../../shared/hooks/useTranslation";

const PAGE_SIZE = 10;

const Search = () => {
  const { groupedStockItems, groupedStockItemsQuery, filters, filtersQuery, loading, success, error } =
    useAppSelector<CustomerBuyerInventoryState>((state: any) => state.customerBuyerInventory);

  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const productNumber = searchParams.get("productNumber");

  const navigate = useNavigate();

  const [pageNo, setPageNo] = useState(1);
  const showMoreButtonRef = useRef<any>(null);
  const showClearFilterButton =
    (filtersQuery.countryIds && filtersQuery.countryIds?.length > 0) ||
    (filtersQuery.manufacturerIds && filtersQuery.manufacturerIds?.length > 0) ||
    (filtersQuery.categoryIds && filtersQuery.categoryIds?.length > 0) ||
    (filtersQuery.conditionCodes && filtersQuery.conditionCodes?.length > 0) ||
    (filtersQuery.groupIds && filtersQuery.groupIds?.length > 0);
  const showFilter = filters.countries?.length > 0 ||
    filters.manufacturers?.length > 0 ||
    filters.categories?.length > 0 ||
    filters.conditions?.length > 0 ||
    filters.groups?.length > 0;

  const loadGroupedStockItems = useCallback(
    (pageNo: number) => {
      dispatch(
        getGroupedStockItems({
          pageNo: pageNo,
          pageSize: PAGE_SIZE,
          number: productNumber || undefined,
          countryIds: filtersQuery.countryIds || [],
          manufacturerIds: filtersQuery.manufacturerIds || [],
          categoryIds: filtersQuery.categoryIds || [],
          conditionCodes: filtersQuery.conditionCodes || [],
          groupIds: filtersQuery.groupIds || []
        })
      );
    },
    [dispatch, getGroupedStockItems, groupedStockItemsQuery, filtersQuery, PAGE_SIZE, productNumber]
  );

  const handleClearFilter = () => {
    dispatch(
      changeProductFiltersQuery({
        ...filtersQuery,
        number: productNumber || undefined,
        countryIds: [],
        manufacturerIds: [],
        categoryIds: [],
        conditionCodes: [],
        groupIds: []
      })
    );
  };

  const handleShowMoreStockItems = useCallback(() => {
    setPageNo((oldValue) => {
      const newValue = oldValue + 1;
      loadGroupedStockItems(newValue);
      return newValue;
    });
  }, [setPageNo, loadGroupedStockItems]);

  const handleShowSuppliers = (item: GroupedStockItem) => {
    navigate(`/search/${item.id}/${item.number}?productNumber=${productNumber}`);
  };

  useEffect(() => {
    setPageNo(1);
    dispatch(getFilters({ ...filtersQuery, number: productNumber || undefined }));
    dispatch(setGroupedStockItems({ pageNo: 0, pageSize: 0, items: [], totalCount: 0, totalPages: 0 }));
    loadGroupedStockItems(1);
  }, [productNumber, filtersQuery]);

  useLayoutEffect(() => {
    if (showMoreButtonRef.current) {
      const currentElement = showMoreButtonRef.current;
      let observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !loading) {
            handleShowMoreStockItems();
          }
        });
      });

      observer.observe(currentElement);

      return () => {
        observer.disconnect();
      };
    }
  }, [pageNo, showMoreButtonRef.current, loading, handleShowMoreStockItems]);

  const { translate } = useTranslation("marketplace");

  return (
    <>
      <ActionToast
        open={!!success}
        variant="success"
        message={success || ""}
        onClose={() => dispatch(setSuccess(undefined))}
        duration={6000}
      />

      <ActionToast
        open={!!error}
        variant="error"
        message={NotificationMessage.error(error).message || ""}
        onClose={() => dispatch(setError(undefined))}
        duration={6000}
      />

      {productNumber ? (
        <Box className="w-full flex" sx={{ marginTop: "70px" }}>
          {showFilter && (
            <Box className="px-4" sx={{ width: "240px", paddingTop: "20px" }}>
              <Box className="flex items-center justify-between">
                <Box className="text-18-bold" color="#333333" sx={{ padding: "6px 0" }}>
                  {translate("filterBy")}
                </Box>

                {showClearFilterButton && (
                  <Button variant="text" className="text-14-medium" onClick={handleClearFilter}>
                    {translate("clearAll")}
                  </Button>
                )}
              </Box>

              <Box className="mt-4">
                <Filters />
              </Box>
            </Box>
          )}

          <Box className="w-full px-4 pt-6 pb-4 v-fill">
            <Box className="flex gap-4 items-center">
              <Box className="text-20-bold">{productNumber}</Box>
              <Box className="text-14-regular">
                {translate("weFoundResults", { count: groupedStockItems?.totalCount })}
              </Box>
            </Box>

            <Box>
              {groupedStockItems.items.length ? (
                <Box className="flex gap-2 flex-wrap mt-4">
                  {groupedStockItems?.items.map((item, i) => (
                    <GroupedStockItemCard key={i} item={item} onShowSuppliers={() => handleShowSuppliers(item)} />
                  ))}
                </Box>
              ) : (
                <>{loading && <Loader />}</>
              )}
            </Box>

            {groupedStockItems.items.length < groupedStockItems.totalCount && (
              <Box ref={showMoreButtonRef} className="flex justify-center w-full mt-2">
                {loading && <Loader />}
              </Box>
            )}
          </Box>
        </Box>
      ) : (
        <EmptySearchAndBuy />
      )}
    </>
  );
};

export default Search;